import { Workflow } from '@/types/workflow';
import { api } from '@/lib/api';

/**
 * Service for importing local workflows into the database
 */
export const workflowImportService = {
  /**
   * List all local workflows available for import
   * Note: This requires local workflow source files to be accessible
   * In the admin panel, we'll fetch this list from the app's local registry
   */
  async listLocalWorkflows(): Promise<Workflow[]> {
    // In a real implementation, we would need access to the app package's local workflows
    // For now, we'll fetch from an endpoint that reads the local workflow files
    // This could be implemented as a special admin endpoint that reads from
    // packages/app/src/features/workflows/local/workflows/

    // Placeholder: Return empty array
    // TODO: Implement endpoint to read local workflow files from filesystem
    return [];
  },

  /**
   * Check if a workflow with the given slug exists in the database
   */
  async checkWorkflowExists(slug: string): Promise<{ exists: boolean; workflow?: Workflow }> {
    try {
      // URL encode the slug to handle special characters like dots
      const encodedSlug = encodeURIComponent(slug);
      const response = await api.get(`/workflows/${encodedSlug}`);
      if (response.data.success && response.data.data) {
        return { exists: true, workflow: response.data.data };
      }
      return { exists: false };
    } catch (error: any) {
      if (error.response?.status === 404) {
        return { exists: false };
      }
      throw error;
    }
  },

  /**
   * Transform local workflow to database format
   * Replaces local-specific fields with DB-appropriate values
   */
  transformLocalToDb(localWorkflow: Workflow, userId: string): Partial<Workflow> {
    return {
      // Keep these as-is
      slug: localWorkflow.slug,
      title: localWorkflow.title,
      description: localWorkflow.description,
      aim: localWorkflow.aim,
      why: localWorkflow.why,
      imageUrl: localWorkflow.imageUrl,
      definition: localWorkflow.definition,
      tags: localWorkflow.tags,
      category: localWorkflow.category,
      isPublic: localWorkflow.isPublic,
      isTemplate: localWorkflow.isTemplate,

      // Transform these
      userId: userId, // Replace 'local-dev' with actual user

      // Add metadata to track import
      metadata: {
        ...localWorkflow.metadata,
        importedFrom: 'local',
        importedFromLocal: true,
        originalLocalId: localWorkflow.id,
        lastSyncedAt: new Date().toISOString(),
      },

      // Let DB generate these fields automatically
      // id, version, createdAt, updatedAt, usageCount
    };
  },

  /**
   * Import a local workflow into the database
   * Creates a new workflow record
   */
  async importWorkflow(localWorkflow: Workflow, userId: string): Promise<Workflow> {
    const transformedData = this.transformLocalToDb(localWorkflow, userId);

    const response = await api.post('/workflows', transformedData);

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to import workflow');
    }

    return response.data.data;
  },

  /**
   * Update an existing database workflow from a local workflow
   * Preserves the existing workflow's ID and increments version
   * Also preserves any custom images that were added
   */
  async updateFromLocal(workflowId: string, localWorkflow: Workflow, userId: string): Promise<Workflow> {
    // First fetch the existing workflow to preserve images
    const existingResponse = await api.get(`/workflows/${workflowId}`);
    if (!existingResponse.data.success) {
      throw new Error('Failed to fetch existing workflow');
    }
    const existingWorkflow = existingResponse.data.data;

    const transformedData = this.transformLocalToDb(localWorkflow, userId);

    // For updates, don't change the userId - keep the existing owner
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { userId: _userId, ...updateDataWithoutUserId } = transformedData;

    // Preserve custom images from existing workflow
    const preservedImages = this.preserveCustomImages(existingWorkflow, transformedData);

    // Update the metadata to track the sync
    const updateData = {
      ...updateDataWithoutUserId,
      ...preservedImages,
      metadata: {
        ...transformedData.metadata,
        importedFromLocal: true,
        lastSyncedAt: new Date().toISOString(),
      },
    };

    const response = await api.put(`/workflows/${workflowId}`, updateData);

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to update workflow');
    }

    return response.data.data;
  },

  /**
   * Preserve custom images that were added in the database
   * Only preserves images that are AssetImage objects (uploaded), not string URLs
   */
  preserveCustomImages(existingWorkflow: Workflow, newWorkflow: any): {
    imageUrl?: string | any;
    definition: any;
  } {
    const result: any = {};

    // Helper to check if an image is a custom uploaded image (AssetImage object)
    const isCustomImage = (image: any): boolean => {
      return image && typeof image === 'object' && 'light' in image && 'dark' in image;
    };

    // Preserve workflow cover image if it's custom
    if (isCustomImage(existingWorkflow.imageUrl) && !isCustomImage(newWorkflow.imageUrl)) {
      result.imageUrl = existingWorkflow.imageUrl;
    }

    // Preserve definition imageUrl if it's custom
    const definition = { ...newWorkflow.definition };
    if (isCustomImage(existingWorkflow.definition?.imageUrl) && !isCustomImage(definition.imageUrl)) {
      definition.imageUrl = existingWorkflow.definition.imageUrl;
    }

    // Preserve step images if they're custom
    if (definition.steps && existingWorkflow.definition?.steps) {
      definition.steps = definition.steps.map((newStep: any, index: number) => {
        const existingStep = existingWorkflow.definition.steps[index];
        if (existingStep && isCustomImage(existingStep.imageUrl) && !isCustomImage(newStep.imageUrl)) {
          return {
            ...newStep,
            imageUrl: existingStep.imageUrl,
          };
        }
        return newStep;
      });
    }

    result.definition = definition;
    return result;
  },

  /**
   * Compare local workflow with database workflow to detect changes
   * Returns a summary of differences
   */
  detectChanges(localWorkflow: Workflow, dbWorkflow: Workflow): {
    hasChanges: boolean;
    changes: string[];
  } {
    const changes: string[] = [];

    // Compare key fields
    if (localWorkflow.title !== dbWorkflow.title) {
      changes.push(`Title: "${dbWorkflow.title}" → "${localWorkflow.title}"`);
    }

    if (localWorkflow.description !== dbWorkflow.description) {
      changes.push(`Description changed`);
    }

    if (JSON.stringify(localWorkflow.definition) !== JSON.stringify(dbWorkflow.definition)) {
      changes.push('Workflow definition updated');
    }

    if (JSON.stringify(localWorkflow.tags) !== JSON.stringify(dbWorkflow.tags)) {
      changes.push(`Tags: ${dbWorkflow.tags?.join(', ') || '(none)'} → ${localWorkflow.tags?.join(', ') || '(none)'}`);
    }

    if (localWorkflow.category !== dbWorkflow.category) {
      changes.push(`Category: ${dbWorkflow.category || '(none)'} → ${localWorkflow.category || '(none)'}`);
    }

    if (localWorkflow.isPublic !== dbWorkflow.isPublic) {
      changes.push(`Visibility: ${dbWorkflow.isPublic ? 'Public' : 'Private'} → ${localWorkflow.isPublic ? 'Public' : 'Private'}`);
    }

    return {
      hasChanges: changes.length > 0,
      changes,
    };
  },

  /**
   * Bulk import multiple workflows
   * Returns results for each workflow
   */
  async bulkImport(
    workflows: Workflow[],
    userId: string,
    options: { skipExisting?: boolean } = {}
  ): Promise<{
    successful: Workflow[];
    failed: Array<{ workflow: Workflow; error: string }>;
    skipped: Workflow[];
  }> {
    const successful: Workflow[] = [];
    const failed: Array<{ workflow: Workflow; error: string }> = [];
    const skipped: Workflow[] = [];

    for (const localWorkflow of workflows) {
      try {
        // Check if workflow exists
        const { exists } = await this.checkWorkflowExists(localWorkflow.slug);

        if (exists && options.skipExisting) {
          skipped.push(localWorkflow);
          continue;
        }

        // Import the workflow
        const imported = await this.importWorkflow(localWorkflow, userId);
        successful.push(imported);
      } catch (error: any) {
        failed.push({
          workflow: localWorkflow,
          error: error.message || 'Unknown error',
        });
      }
    }

    return { successful, failed, skipped };
  },
};
